'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { NOTIFICATION_CONFIG } from '@website/config/notifications.config';
import { useUserType } from '@website/hooks/useCurrentUser';
import { useNotifications } from '@website/hooks/useNotifications';
import { invalidateCachesForNotification } from '@website/lib/notificationCacheInvalidation';

/**
 * Watches the authenticated user's notification stream and invalidates
 * the related React Query caches whenever a new notification arrives.
 *
 * This is what keeps domain views (bookings, activities, profile, payments…)
 * in sync with notification-driven state changes — e.g. an activity that has
 * just been cancelled by the professional, or a profile that has just been
 * approved by an admin — without forcing the user to refresh the page or
 * wait for the per-query stale time to expire.
 *
 * Implementation notes:
 *  - Uses the same query parameters as `useUnreadCount` so the underlying
 *    HTTP request is shared via React Query's de-duplication; this hook
 *    therefore adds zero network traffic.
 *  - On the very first response we *seed* the seen-IDs set without firing
 *    any invalidations — otherwise we'd nuke caches on every page load.
 *  - The seen-IDs set resets when the user logs out so a subsequent login
 *    starts clean.
 *
 * Mount once near the root via `<NotificationCacheSync />`.
 */
export function useNotificationCacheSync(): void {
  const queryClient = useQueryClient();
  const userType = useUserType();
  const isAuthenticated = userType !== 'public';

  const { data: notifications } = useNotifications(
    { limit: NOTIFICATION_CONFIG.DROPDOWN_NOTIFICATION_LIMIT },
    { enablePolling: true, enabled: isAuthenticated }
  );

  const seenIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!notifications) return;

    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(notifications.map((n) => n.id));
      return;
    }

    const seen = seenIdsRef.current;
    const fresh = notifications.filter((n) => !seen.has(n.id));
    if (fresh.length === 0) return;

    for (const notification of fresh) {
      seen.add(notification.id);
      invalidateCachesForNotification(queryClient, notification);
    }
  }, [notifications, queryClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      seenIdsRef.current = null;
    }
  }, [isAuthenticated]);
}
